import React from 'react';
import classNames from 'classnames';
import { Field, FieldProps } from 'formik';

import TextRow, { TextRowType } from 'components/UI/Form/Row/TextRow/TextRow';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { useField } from '../hooks/useField';

import styles from './FormikTextRow.module.scss';

export type FormikTextRowType = Omit<TextRowType, 'value'> & {
  beforeOnChange?: (e: React.ChangeEvent) => void;
  afterOnChange?: (e: React.ChangeEvent) => void;
  aroundOnChange?: (
    onChange: (e: React.ChangeEvent) => void,
    e: React.ChangeEvent
  ) => void;
  afterOnBlur?: (e: React.ChangeEvent) => void;
  component?: React.ElementType;
  classes?: TextRowType['classes'] & {
    errorMessage?: string;
    row?: string;
  };
};

export default function FormikTextRow({
  inputProps,
  component,
  classes,
  ...props
}: FormikTextRowType): JSX.Element {
  const { name } = inputProps;
  const { errorMessage, row, ...allClasses } = classes || {};
  const [field, { touched, error: formikError, apiError }] = useField(name);
  const error = formikError && touched ? formikError : apiError || undefined;

  return (
    <div className={row}>
      <Field
        field={field}
        name={name}
        inputProps={{ ...inputProps, error: !!error }}
        component={component || TextRowComponent}
        classes={allClasses}
        {...props}
      />
      <ErrorMessage
        name={name}
        additionalClass={classNames(styles.errorMessage, errorMessage)}
      />
    </div>
  );
}

export type ComponentProps = FormikTextRowType & FieldProps;

export function TextRowComponent({
  inputProps,
  field,
  aroundOnChange,
  beforeOnChange,
  afterOnChange,
  afterOnBlur,
  ...props
}: ComponentProps): JSX.Element {
  const { onChange, onBlur, ...formikField } = { ...field };
  const handleChange = (e: React.ChangeEvent) => {
    const executeChange = (event: React.ChangeEvent) => {
      beforeOnChange?.(event);
      onChange(event);
      afterOnChange?.(event);
    };

    if (aroundOnChange) {
      aroundOnChange(executeChange, e);
    } else {
      executeChange(e);
    }
  };

  return (
    <TextRow
      {...props}
      inputProps={{
        onChange: handleChange,
        onBlur: e => {
          onBlur(e);
          afterOnBlur && afterOnBlur(e);
        },
        ...formikField,
        ...inputProps,
      }}
    />
  );
}
